import { useMutation, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "@/api/apiClient";
import {
  mergeFilesApiV1FilesMergePost,
  unmergeFileApiV1FilesIdUnmergePost,
} from "@/api/generated/sdk.gen";
import type { MergeFilesApiV1FilesMergePostData } from "@/api/generated/types.gen";
import { CLIENT_KEYS } from "@/constants/clientKeys";
import { buildApiErrorMessage } from "@/utils/notification";

export const useMergeFiles = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: MergeFilesApiV1FilesMergePostData["body"]) => {
      const response = await mergeFilesApiV1FilesMergePost({
        body: data,
        client: apiClient,
      });

      if ("error" in response && response.error) {
        throw new Error(
          buildApiErrorMessage("Failed to merge files", response.error)
        );
      }

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [CLIENT_KEYS.FILES],
      });
      queryClient.invalidateQueries({
        queryKey: [CLIENT_KEYS.ACTIVITY_LOGS],
      });
    },
    onError: (error) => {
      console.error("Error merging files:", error);
    },
  });
};

export const useUnmergeFile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (mergedFileId: string) => {
      const response = await unmergeFileApiV1FilesIdUnmergePost({
        path: { id: mergedFileId },
        client: apiClient,
      });

      if ("error" in response && response.error) {
        throw new Error(
          buildApiErrorMessage("Failed to unmerge file", response.error)
        );
      }

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [CLIENT_KEYS.FILES],
      });
      queryClient.invalidateQueries({
        queryKey: [CLIENT_KEYS.ACTIVITY_LOGS],
      });
    },
    onError: (error) => {
      console.error("Error unmerging file:", error);
    },
  });
};
